import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import { searchOrganizations } from "../Manage-Team/api";
import TableSecondary from "../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";

// Cross-tenant org search (Ascend Cargo System Admin only) — reuses the existing
// GET /api/organizations/search endpoint, already unscoped by office and already searched/
// paginated across every org. Modeled on Manage-Team/Modals/AddPreferedPartner.js's debounced
// search, without the "invite a new org" branch (not relevant here).
const OrgSearchPicker = ({ onSelect }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const delay = setTimeout(async () => {
            if (!query.trim()) {
                setResults([]);
                return;
            }
            setLoading(true);
            try {
                const res = await searchOrganizations(query);
                const mapped = Array.isArray(res.data?.content)
                    ? res.data.content.map((org) => ({
                        name: org.name || "Unnamed",
                        officeCode: org.officeCode,
                        location: org.location,
                    }))
                    : [];
                setResults(mapped);
            } catch (err) {
                console.error("Org search failed:", err);
                toast.error("Failed to search organizations");
            } finally {
                setLoading(false);
            }
        }, 400);
        return () => clearTimeout(delay);
    }, [query]);

    return (
        <div className="space-y-fluid-2">
            <div className="relative w-full max-w-[480px]">
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search any organization by name or office code..."
                    className="w-full border border-gray-300 dark:border-acs-dark-border rounded-xl pl-9 pr-3 py-2 text-fluid-sm bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text placeholder-gray-400 dark:placeholder-acs-dark-subtext focus:outline-none focus:border-[#223688] dark:focus:border-[#31C4AB] shadow-sm"
                />
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#223688] dark:text-[#3B5CCC]" />
            </div>

            <div className="max-h-[45vh] overflow-y-auto">
                {loading ? (
                    <Loader color="text-blue-600" message="Searching organizations..." />
                ) : results.length > 0 ? (
                    <TableSecondary
                        headers={["Name", "Office Code", "Location"]}
                        columnWidths={["45%", "25%", "30%"]}
                        data={results}
                        renderRow={(row) => (
                            <>
                                <td
                                    className="px-fluid-3 py-fluid-2 text-fluid-sm text-[#030229] dark:text-acs-dark-text cursor-pointer"
                                    onClick={() => onSelect(row)}
                                >
                                    {row.name}
                                </td>
                                <td
                                    className="px-fluid-3 py-fluid-2 text-fluid-sm font-roboto text-gray-700 dark:text-acs-dark-text cursor-pointer"
                                    onClick={() => onSelect(row)}
                                >
                                    {row.officeCode}
                                </td>
                                <td
                                    className="px-fluid-3 py-fluid-2 text-fluid-sm text-gray-600 dark:text-acs-dark-subtext cursor-pointer"
                                    onClick={() => onSelect(row)}
                                >
                                    {row.location?.city && row.location?.state
                                        ? `${row.location.city}, ${row.location.state}`
                                        : "—"}
                                </td>
                            </>
                        )}
                    />
                ) : query ? (
                    <p className="p-4 text-gray-400 dark:text-acs-dark-subtext text-sm text-center">No organizations found</p>
                ) : (
                    <p className="p-4 text-gray-400 dark:text-acs-dark-subtext text-sm text-center">
                        Start typing to find any organization on the platform
                    </p>
                )}
            </div>
        </div>
    );
};

export default OrgSearchPicker;
